import type { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import {
  getConversionFactorValue,
  sumConversionFactors,
} from '@shared/helpers/conversion-factors.helpers';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { getScientificDisplayNumber } from '@shared/helpers/display/get-scientific-display-number.helper';
import { BASELINE_COLOR } from '@shared/helpers/proposals/proposal-colors.constants';
import type { IProposal } from '@shared/models/proposals.model';
import type { Results, ResultsRecord } from '@shared/models/unit.model';
import { UNICODE } from '@shared/models/unicode.model';
import { getCo2eChartDataFromResults } from '@ui/BarChart/helpers/bar-chart.helpers';

import type { ProposalBar } from './ElementListRow';

type CurrencyDisplay = ReturnType<typeof useDisplayCurrency>;

type GetResultsPerGFA = (results: Results | undefined) => Results;

/** Inputs shared when deciding whether to show a grey baseline bar behind a proposal bar. */
export type BaselineCo2BehindProposalBarContext = {
  selectedProposalId: IProposal['id'] | null | undefined;
  proposalColor: string;
  hasSelectedVersion: boolean;
};

/**
 * Whether a row should render the grey pre-proposal CO₂ bar behind the proposal bar.
 */
export const shouldShowBaselineCo2BehindProposalBar = ({
  selectedProposalId,
  proposalColor,
  hasSelectedVersion,
}: BaselineCo2BehindProposalBarContext): boolean =>
  selectedProposalId != null &&
  proposalColor !== BASELINE_COLOR &&
  hasSelectedVersion;

/**
 * Per-GFA co2e from one element's lifecycle-filtered baseline results (unresolved version record).
 */
export const getBaselineCo2ValuePerGfaForElement = (
  elementId: string,
  baselineResultRecord: ResultsRecord,
  getResultsPerGFA: GetResultsPerGFA,
): number | undefined => {
  const baselineResults = baselineResultRecord[elementId];
  if (!baselineResults) return undefined;
  return (
    getConversionFactorValue(getResultsPerGFA(baselineResults), 'co2e') ??
    undefined
  );
};

/**
 * Per-GFA co2e summed across item ids from the lifecycle-filtered baseline record.
 */
export const getBaselineCo2ValuePerGfaForItemIds = (
  itemIds: readonly string[],
  baselineResultRecord: ResultsRecord,
  getResultsPerGFA: GetResultsPerGFA,
): number | undefined =>
  getConversionFactorValue(
    getResultsPerGFA(
      sumConversionFactors(
        ...itemIds.map((id) => baselineResultRecord[id] ?? {}),
      ),
    ),
    'co2e',
  ) ?? undefined;

/**
 * Attaches `baselineCo2Value` when a proposal is active and baseline CO₂ is known.
 */
export const attachBaselineCo2BehindProposalBar = (
  bar: ProposalBar,
  context: BaselineCo2BehindProposalBarContext & {
    baselineCo2Value: number | undefined;
  },
): ProposalBar => {
  if (!shouldShowBaselineCo2BehindProposalBar(context)) return bar;
  if (context.baselineCo2Value == null) return bar;
  return { ...bar, baselineCo2Value: context.baselineCo2Value };
};

/**
 * Below this absolute value the CO₂/cost KPI columns render an en dash instead
 * of a number, surfacing the raw magnitude as scientific notation in the
 * tooltip. Compact notation can't shrink sub-unit values, so tiny results would
 * otherwise leak as raw decimals (e.g. `0.0000000000003`).
 */
const KPI_DASH_UNDER_THRESHOLD = 1e-3;

export type CompactKpiDisplay = {
  /** Compact string (e.g. `"34K"`), or an en dash for very small non-zero values. */
  text: string;
  /**
   * Scientific notation (e.g. `"3.2 × 10⁻⁸"`) shown in the column tooltip when
   * {@link CompactKpiDisplay.text} was dashed; `undefined` otherwise.
   */
  scientificTooltip: string | undefined;
};

/**
 * Formats a CO₂/cost KPI value for the element list's right-hand columns:
 * compact notation, except very small non-zero magnitudes (abs below
 * {@link KPI_DASH_UNDER_THRESHOLD}) which render as an en dash with the value
 * exposed as scientific notation in {@link CompactKpiDisplay.scientificTooltip}.
 */
export const getCompactKpiDisplay = (value: number): CompactKpiDisplay => {
  if (value !== 0 && Math.abs(value) < KPI_DASH_UNDER_THRESHOLD)
    return {
      text: UNICODE.DASH.EN_DASH,
      scientificTooltip: getScientificDisplayNumber(value),
    };
  return {
    text: getDisplayNumber(value, { notation: 'compact' }),
    scientificTooltip: undefined,
  };
};

/**
 * Builds a single {@link ProposalBar} from per-GFA results, formatting CO2 and
 * cost the way {@link ElementListRow} expects, and forwarding the bar `color`
 * (e.g. v1 per-proposal color or the selected/baseline overlay).
 *
 * Returns both the compact display strings (`co2`, `cost`; an en dash for tiny
 * values, with scientific notation in `*ScientificTooltip`) and the raw numeric
 * values (`co2Value`, `costValue`) so the bar width can be computed against
 * the row's `maxCo2`/`maxCost` without going through `parseFloat` on a
 * suffixed string like "1.2K" (which silently strips the unit).
 */
export const buildProposalBarEntry = (
  perGFA: Results,
  color: string,
  displayCurrency: CurrencyDisplay,
): ProposalBar => {
  const co2 = getConversionFactorValue(perGFA, 'co2e');
  const sek = getConversionFactorValue(perGFA, 'sek');
  const cost =
    displayCurrency.isEnabled && sek != null
      ? getDisplayValueData(
          { value: sek, unit: 'sek' },
          {
            outputUnit: { unit: displayCurrency.current },
            notation: 'compact',
          },
        ).valueAsNumber
      : null;
  const co2Display = co2 != null ? getCompactKpiDisplay(co2) : null;
  const costDisplay = cost != null ? getCompactKpiDisplay(cost) : null;
  return {
    co2: co2Display?.text ?? '0',
    cost: costDisplay?.text ?? '',
    co2ScientificTooltip: co2Display?.scientificTooltip,
    costScientificTooltip: costDisplay?.scientificTooltip,
    co2Value: co2 ?? 0,
    costValue: cost ?? 0,
    color,
    co2Segments: getCo2eChartDataFromResults(perGFA),
  };
};
